/**
 * D-263 (2026-10-23) — GMX_RENEWAL_MAX_INFLIGHT on the admin renewal starters.
 *
 * The api caps a provider's renewal jobs that can run now: running, or queued
 * and due (gmx: 60; D-264: a job parked until a later retry time does not
 * count). The bulk starters trim their selection to the remaining headroom and
 * report it (`matchedAccountCount`, `skippedInflightCap`, `skippedInflightJob`,
 * `renewalInflight`); with no headroom an execute answers
 * 409 `{ detail: 'renewal_inflight_cap_reached', renewalInflight }`. D-264: the
 * single-parent starters also answer 409 `{ detail: 'renewal_job_in_flight' }`
 * for a parent that already has an open job. The api leaves the Turkish copy
 * to the admin UI: this module owns it.
 */
import type { ApiResult } from '../../api/client.js';

export const RENEWAL_CAP_DETAIL = 'renewal_inflight_cap_reached';
/** D-264 — a single-parent starter refused a parent that already has an open job. */
export const RENEWAL_JOB_IN_FLIGHT_DETAIL = 'renewal_job_in_flight';

export interface RenewalInflightWire {
  readonly cap: number;
  readonly inflight: number;
  readonly headroom: number;
}

/** The optional cap fields a bulk starter adds for a capped provider. */
export interface RenewalCapFields {
  readonly matchedAccountCount?: number;
  readonly skippedInflightCap?: number;
  readonly skippedInflightJob?: number;
  readonly renewalInflight?: RenewalInflightWire;
}

function readInflight(body: Record<string, unknown> | null): RenewalInflightWire | null {
  const raw = body?.['renewalInflight'];
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const n = (k: string): number | null => (typeof r[k] === 'number' ? (r[k] as number) : null);
  const cap = n('cap');
  const inflight = n('inflight');
  const headroom = n('headroom');
  return cap === null || inflight === null || headroom === null ? null : { cap, inflight, headroom };
}

/**
 * The api client reduces an error body without a `reason` key to `http_409`.
 * This keeps the cap refusal recognisable: reason
 * `renewal_inflight_cap_reached:<inflight>/<cap>` (or the bare detail).
 */
export function withRenewalCapReason<T>(res: ApiResult<T>): ApiResult<T> {
  if (res.kind !== 'error' || res.status !== 409) return res;
  if (res.body?.['detail'] === RENEWAL_JOB_IN_FLIGHT_DETAIL) return { ...res, reason: RENEWAL_JOB_IN_FLIGHT_DETAIL };
  if (res.body?.['detail'] !== RENEWAL_CAP_DETAIL) return res;
  const inflight = readInflight(res.body);
  const reason = inflight ? `${RENEWAL_CAP_DETAIL}:${inflight.inflight}/${inflight.cap}` : RENEWAL_CAP_DETAIL;
  return { ...res, reason };
}

/** Turkish text for a mutation error reason (the cap refusal), else the reason itself. */
export function renewalErrorText(reason: string): string {
  if (reason === RENEWAL_CAP_DETAIL || reason.startsWith(`${RENEWAL_CAP_DETAIL}:`)) {
    const counts = reason.slice(RENEWAL_CAP_DETAIL.length + 1);
    return `Yenileme kuyruğu dolu${counts ? ` (${counts})` : ''}; kuyruk boşalınca tekrar deneyin.`;
  }
  if (reason === RENEWAL_JOB_IN_FLIGHT_DETAIL) {
    return 'Bu parent zaten yenileme kuyruğunda; iş bitince tekrar deneyin.';
  }
  return reason;
}

/**
 * The confirm / result lines for a capped bulk run, or [] when the provider is
 * not capped or nothing was left out. `willProcess` = parents the run handles.
 */
export function renewalCapLines(d: RenewalCapFields, willProcess: number): string[] {
  const lines: string[] = [];
  const skippedCap = d.skippedInflightCap ?? 0;
  const busy = d.skippedInflightJob ?? 0;
  if (skippedCap > 0) {
    const cap = d.renewalInflight?.cap;
    lines.push(
      `Yenileme kuyruğu sınırı${cap !== undefined ? ` (${cap})` : ''}: yalnız ${willProcess} parent işlenecek, ` +
        `${skippedCap} parent sonraki çalıştırmaya kaldı.`,
    );
  }
  if (busy > 0) lines.push(`${busy} parent zaten yenileme kuyruğunda — atlandı.`);
  return lines;
}
